import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { printReceipt, kickDrawer, PrintOutcome } from "../../services/printing";

interface Sale {
  id: string;
  subtotal_cash: number;
  subtotal_card: number;
  tax_cash: number;
  tax_card: number;
  total_cash: number;
  total_card: number;
  total_discount: number;
}

interface PaymentModalProps {
  sale: Sale;
  onComplete: () => void;
  onClose: () => void;
  trainingMode?: boolean;
}

type TerminalState = "idle" | "sending" | "waiting" | "approved" | "declined" | "timeout" | "error";

export default function PaymentModal({ sale, onComplete, onClose, trainingMode }: PaymentModalProps) {
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [completedSale, setCompletedSale] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printOutcome, setPrintOutcome] = useState<PrintOutcome | null>(null);
  const [showGiftCard, setShowGiftCard] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftApplied, setGiftApplied] = useState(0);
  const [giftRemaining, setGiftRemaining] = useState<number | null>(null);
  const [giftBusy, setGiftBusy] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState<boolean | null>(null);
  const [terminalState, setTerminalState] = useState<TerminalState>("idle");
  const [cardLockedAfterTimeout, setCardLockedAfterTimeout] = useState(false);
  // A tip the customer asked to add, charged with the card (never on cash)
  const [tip, setTip] = useState(0);
  const [showTip, setShowTip] = useState(false);
  const [tipInput, setTipInput] = useState("");

  // Split payment state
  const [showSplit, setShowSplit] = useState(false);
  const [splitCashInput, setSplitCashInput] = useState("");
  const [splitCashCollected, setSplitCashCollected] = useState(0);
  const [splitCardRemaining, setSplitCardRemaining] = useState(0);
  const [splitStep, setSplitStep] = useState<"enter_cash" | "collect_card" | "done">("enter_cash");

  // Partial auth state
  const [partialAuthMsg, setPartialAuthMsg] = useState<string | null>(null);
  const [remainingAfterPartial, setRemainingAfterPartial] = useState(0);

  const totalCash = Number(sale.total_cash);
  const totalCard = Number(sale.total_card);
  const savings = (totalCard - totalCash).toFixed(2);
  const totalDiscount = Number(sale.total_discount || 0);
  const fmt = (n: number) => "$" + n.toFixed(2);

  // Send the amount to the card terminal and wait for the card. Helcim answers
  // asynchronously: /charge waits a while, and if the card has not been tapped
  // yet it hands back "pending" with an attempt to poll until it settles.
  const chargeAndWait = async (amountCents: number, method: string, tipCents = 0) => {
    const first = await api.post("/payments/charge", {
      sale_id: sale.id,
      amount_cents: amountCents + tipCents,
      payment_method: method,
      ...(tipCents > 0 ? { tip_cents: tipCents } : {}),
    }, { timeout: 100_000 });
    let r = first.data;
    const giveUp = Date.now() + 150_000;
    while (r.status === "pending" && r.attemptId && Date.now() < giveUp) {
      await new Promise((res) => setTimeout(res, 2000));
      try {
        r = (await api.get(`/payments/attempts/${r.attemptId}`, { timeout: 30_000 })).data;
      } catch {
        // A dropped poll is not a result; keep asking.
      }
    }
    if (r.status === "pending") return { ...r, status: "timeout" };
    return r;
  };

  // ── Full payment (cash, debit, credit — not split) ──
  const processPayment = async (method: string) => {
    setProcessing(true);
    setSelectedMethod(method);
    setError(null);

    try {
      if ((method === "credit" || method === "debit") && !trainingMode) {
        setTerminalState("sending");
        const amountCents = Math.round(
          (method === "debit" ? totalCash : totalCard) * 100
        );

        setTerminalState("waiting");
        const r = await chargeAndWait(amountCents, method, Math.round(tip * 100));

        if (r.status === "timeout") {
          setTerminalState("timeout");
          setCardLockedAfterTimeout(true);
          setError(null);
          setProcessing(false);
          return;
        }

        if (r.status === "partial") {
          // Card authorized for less than requested
          const authorizedDollars = r.amountAuthorized / 100;
          const remainingCents = r.amountRequested - r.amountAuthorized;
          setPartialAuthMsg(
            `Card approved for ${fmt(authorizedDollars)} of ${fmt(r.amountRequested / 100)} requested.`
          );
          setRemainingAfterPartial(remainingCents / 100);
          setTerminalState("idle");
          setProcessing(false);
          return;
        }

        if (r.status !== "approved") {
          setTerminalState("declined");
          setError(r.processorResponse || `Card ${r.status}. Try again or use a different method.`);
          setProcessing(false);
          return;
        }

        setTerminalState("approved");
      }

      // Complete the sale
      const payload: Record<string, unknown> = { payment_method: method };
      const response = await api.post(`/sales/${sale.id}/complete`, payload);
      setCompletedSale(response.data);
      setCompleted(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || "Payment failed. Try again.";
      setError(msg);
      setTerminalState("error");
      setProcessing(false);
    }
  };

  // ── Split: Step 1 — record the cash leg ──
  const recordSplitCash = async () => {
    const cashAmount = parseFloat(splitCashInput);
    if (!cashAmount || cashAmount <= 0 || cashAmount >= totalCash) {
      setError("Cash amount must be between $0.01 and " + fmt(totalCash - 0.01));
      return;
    }
    setProcessing(true);
    setError(null);
    try {
      await api.post("/payments/cash-tender", {
        sale_id: sale.id,
        amount_cents: Math.round(cashAmount * 100),
      });
      setSplitCashCollected(cashAmount);

      // Compute the card remainder: remaining share of cash total + markup on that share
      // This matches the backend computeSplitCardAmount logic
      const cashFraction = cashAmount / totalCash;
      const cardSubtotal = Number(sale.subtotal_cash) * (1 - cashFraction);
      const cardMarkup = cardSubtotal * 0.03; // 3% markup on card portion only
      const cardTax = Number(sale.tax_cash) * (1 - cashFraction);
      const cardCharge = Math.round((cardSubtotal + cardMarkup + cardTax) * 100) / 100;

      setSplitCardRemaining(cardCharge);
      setSplitStep("collect_card");
    } catch {
      setError("Failed to record cash. Try again.");
    } finally {
      setProcessing(false);
    }
  };

  // ── Split: Step 2 — charge the card for the remainder ──
  const chargeSplitCard = async (method: "credit" | "debit") => {
    setProcessing(true);
    setSelectedMethod("split");
    setError(null);

    try {
      // In training mode, skip the terminal entirely
      if (!trainingMode) {
        setTerminalState("sending");
        const amountCents = Math.round(splitCardRemaining * 100);

        setTerminalState("waiting");
        const r = await chargeAndWait(amountCents, method);

        if (r.status === "timeout") {
          setTerminalState("timeout");
          setCardLockedAfterTimeout(true);
          setProcessing(false);
          return;
        }

        if (r.status === "partial") {
          const authorizedDollars = r.amountAuthorized / 100;
          const remainingCents = r.amountRequested - r.amountAuthorized;
        setPartialAuthMsg(
          `Card approved for ${fmt(authorizedDollars)} of ${fmt(splitCardRemaining)} requested.`
        );
        setRemainingAfterPartial(remainingCents / 100);
        setSplitCardRemaining(remainingCents / 100);
        setTerminalState("idle");
        setProcessing(false);
        return;
      }

      if (r.status !== "approved") {
        setTerminalState("declined");
        setError(
          r.processorResponse ||
          `Card ${r.status}. Cash (${fmt(splitCashCollected)}) is safe. Re-tender the card portion.`
        );
        setProcessing(false);
        return;
      }

        setTerminalState("approved");
      } // end if (!trainingMode)

      // Both legs collected — complete the sale
      const response = await api.post(`/sales/${sale.id}/complete`, {
        payment_method: "split",
        split_cash_amount: splitCashCollected,
      });
      setCompletedSale(response.data);
      setCompleted(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || "Card payment failed. Cash is safe. Try the card again.";
      setError(msg);
      setTerminalState("error");
      setProcessing(false);
    }
  };

  // Gift card: prepaid money, so it comes off the CASH total with no card markup.
  // The card is debited straight away and the screen shows what is left to
  // collect; voiding the sale puts it back.
  const applyGift = async () => {
    const code = giftCode.replace(/\s/g, "");
    if (!code) return;
    setGiftBusy(true);
    setError(null);
    try {
      const res = await api.post(`/sales/${sale.id}/gift-card`, { code });
      setGiftApplied(Number(res.data.total_gift_applied));
      setGiftRemaining(Number(res.data.remaining_due_cash));
      setGiftCode("");
      if (res.data.fully_covered) {
        setShowGiftCard(false);
        await processPayment("cash");   // nothing left owing; completes as gift_card
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || "Could not use that gift card");
    }
    setGiftBusy(false);
  };

  // On completion: open the drawer if cash changed hands, then print the receipt.
  // Both are best effort - neither can undo a sale that is already paid for, so a
  // dead print bridge must never look like a failed payment. The screen says what
  // actually happened so the cashier is never left guessing.
  useEffect(() => {
    if (!completed || !completedSale) return;
    let cancelled = false;
    (async () => {
      const tier = String(completedSale.pricing_tier_used || "");
      const method = String(completedSale.payment_method || "");
      const cashInvolved = tier === "cash" || method === "cash" || method === "split";
      if (cashInvolved) {
        const opened = await kickDrawer();
        if (!cancelled) setDrawerOpened(opened);
      }
      setPrinting(true);
      const outcome = await printReceipt(String(completedSale.id));
      if (!cancelled) { setPrintOutcome(outcome); setPrinting(false); }
    })();
    return () => { cancelled = true; };
  }, [completed, completedSale]);

  // ── Completed Screen ──
  if (completed && completedSale) {
    const pricingTier = completedSale.pricing_tier_used as string;
    const amountPaid = Number(completedSale.actual_amount_paid);
    const isCash = pricingTier === "cash";
    const cardBrand = completedSale.card_brand as string || "";
    const cardLast4 = completedSale.card_last4 as string || "";
    const tipPaid = Number(completedSale.tip_amount || 0);

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div className="rounded-2xl shadow-2xl w-full max-w-md p-8 text-center" style={{ background: "var(--rbw-bg-card)" }}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl"
            style={{ background: "var(--rbw-success)", color: "#fff" }}>
            &#10003;
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>Payment Complete</h2>

          <div className="rounded-xl p-4 mb-4 text-left" style={{ background: "var(--rbw-bg)" }}>
            <div className="flex justify-between text-lg mb-1">
              <span style={{ color: "var(--rbw-gray-dark)" }}>Method:</span>
              <span className="font-bold uppercase" style={{ color: "var(--rbw-navy)" }}>
                {selectedMethod}
                {cardBrand && cardLast4 && (
                  <span className="text-sm font-normal ml-1" style={{ color: "var(--rbw-gray-dark)" }}>
                    ({cardBrand} ...{cardLast4})
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between text-2xl font-bold">
              <span style={{ color: "var(--rbw-gray-dark)" }}>Total:</span>
              <span style={{ color: isCash ? "var(--rbw-navy)" : "var(--rbw-blue-dark)" }}>
                {fmt(amountPaid)}
              </span>
            </div>
            {tipPaid > 0 && (
              <div className="flex justify-between text-lg mt-1">
                <span style={{ color: "var(--rbw-gray-dark)" }}>Tip:</span>
                <span className="font-bold" style={{ color: "var(--rbw-success)" }}>
                  {fmt(tipPaid)} <span className="text-sm font-normal">(card charged {fmt(amountPaid + tipPaid)})</span>
                </span>
              </div>
            )}
            {splitCashCollected > 0 && (
              <div className="text-sm mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
                Cash: {fmt(splitCashCollected)} + Card: {fmt(amountPaid - splitCashCollected)}
              </div>
            )}
            {totalDiscount > 0 && (
              <div className="text-sm mt-1 font-medium" style={{ color: "var(--rbw-danger)" }}>
                Discount: -{fmt(totalDiscount)}
              </div>
            )}
            {isCash ? (
              <div className="text-sm mt-2 font-medium" style={{ color: "var(--rbw-gold-dark)" }}>
                Customer saved ${savings} by paying cash
              </div>
            ) : pricingTier !== "mixed" ? (
              <div className="text-sm mt-2" style={{ color: "var(--rbw-gray-dark)" }}>
                Cash price would have been {fmt(totalCash)} (save ${savings})
              </div>
            ) : null}
          </div>

          {(printOutcome || drawerOpened === false) && (
            <div className="text-sm mb-3 space-y-1">
              {printOutcome === "browser" && (
                <div style={{ color: "var(--rbw-gold-dark)" }}>
                  Receipt printer not found - opened a print window instead.
                </div>
              )}
              {printOutcome === "failed" && (
                <div style={{ color: "var(--rbw-danger)" }}>
                  Could not print the receipt. The sale is complete and saved.
                </div>
              )}
              {drawerOpened === false && (
                <div style={{ color: "var(--rbw-gold-dark)" }}>
                  Cash drawer did not open - open it by hand.
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={async () => {
                setPrinting(true);
                setPrintOutcome(await printReceipt(sale.id));
                setPrinting(false);
              }}
              disabled={printing}
              className="flex-1 py-4 rounded-xl font-bold text-lg transition-all active:scale-95"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
            >
              {printing ? "PRINTING..." : printOutcome ? "PRINT AGAIN" : "PRINT RECEIPT"}
            </button>
            <button
              onClick={onComplete}
              className="flex-1 py-4 rounded-xl font-bold text-lg transition-all active:scale-95"
              style={{ background: "var(--rbw-navy)", color: "#fff" }}
            >
              NEW SALE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Terminal Waiting Screen ──
  if (terminalState === "waiting" || terminalState === "sending") {
    const isInSplit = splitStep === "collect_card";
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div className="rounded-2xl shadow-2xl w-full max-w-md p-8 text-center" style={{ background: "var(--rbw-bg-card)" }}>
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ background: "var(--rbw-blue)", color: "#fff" }}>
            <svg className="w-10 h-10 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
            {terminalState === "sending" ? "Sending to Terminal..." : "Tap or Insert Card"}
          </h2>
          <p className="text-lg mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
            Card charge: {fmt(isInSplit ? splitCardRemaining : (selectedMethod === "debit" ? totalCash : totalCard) + tip)}
          </p>
          {!isInSplit && tip > 0 && (
            <p className="text-sm mb-2" style={{ color: "var(--rbw-success)" }}>Includes a {fmt(tip)} tip</p>
          )}
          {isInSplit && (
            <p className="text-sm mb-2" style={{ color: "var(--rbw-navy)" }}>
              Cash already collected: {fmt(splitCashCollected)}
            </p>
          )}
          <p className="text-sm" style={{ color: "var(--rbw-gray)" }}>
            Waiting for customer on the terminal...
          </p>
          <div className="mt-6">
            <div className="w-48 h-1.5 rounded-full mx-auto overflow-hidden" style={{ background: "var(--rbw-gray-light)" }}>
              <div className="h-full rounded-full animate-pulse" style={{ background: "var(--rbw-blue)", width: "60%" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Split: Collect Card Step ──
  if (showSplit && splitStep === "collect_card") {
    const cardDisabled = processing || cardLockedAfterTimeout;
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div className="rounded-2xl shadow-2xl w-full max-w-md p-8" style={{ background: "var(--rbw-bg-card)" }}>
          <h2 className="text-2xl font-bold text-center mb-2" style={{ color: "var(--rbw-navy)" }}>
            Split Payment — Card Portion
          </h2>

          <div className="rounded-lg p-4 mb-4" style={{ background: "var(--rbw-bg)" }}>
            <div className="flex justify-between text-sm mb-1">
              <span style={{ color: "var(--rbw-gray-dark)" }}>Cash collected:</span>
              <span className="font-bold" style={{ color: "var(--rbw-success)" }}>{fmt(splitCashCollected)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span style={{ color: "var(--rbw-navy)" }}>Card remaining:</span>
              <span style={{ color: "var(--rbw-blue-dark)" }}>{fmt(splitCardRemaining)}</span>
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
              (includes 3% card service fee on the card portion only)
            </div>
          </div>

          {/* Timeout warning */}
          {terminalState === "timeout" && cardLockedAfterTimeout && (
            <div className="mb-4 p-3 rounded-xl" style={{ background: "#FEF2F2", border: "2px solid #FECACA" }}>
              <div className="font-bold text-xs mb-1" style={{ color: "#DC2626" }}>
                TERMINAL TIMED OUT — CHARGE STATUS UNKNOWN
              </div>
              <div className="text-xs mb-2" style={{ color: "#991B1B" }}>
                Cash ({fmt(splitCashCollected)}) is safe. Check Helcim for the card charge before retrying.
              </div>
              <button
                onClick={() => { setCardLockedAfterTimeout(false); setTerminalState("idle"); }}
                className="w-full py-2 rounded-lg font-bold text-xs"
                style={{ background: "#DC2626", color: "#fff" }}
              >
                I checked Helcim — no charge — unlock card
              </button>
            </div>
          )}

          {/* Partial auth message */}
          {partialAuthMsg && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "#FEF3C7", border: "1px solid var(--rbw-gold)" }}>
              <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>{partialAuthMsg}</div>
              <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
                Collect the remaining {fmt(remainingAfterPartial)} by another method.
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm font-medium"
              style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => chargeSplitCard("debit")}
              disabled={cardDisabled}
              className="py-6 rounded-xl font-bold text-lg transition-all active:scale-95 disabled:opacity-30"
              style={{ background: cardLockedAfterTimeout ? "var(--rbw-gray-light)" : "var(--rbw-gold-light)", color: "var(--rbw-navy)" }}
            >
              DEBIT
            </button>
            <button
              onClick={() => chargeSplitCard("credit")}
              disabled={cardDisabled}
              className="py-6 rounded-xl font-bold text-lg transition-all active:scale-95 disabled:opacity-30"
              style={{ background: cardLockedAfterTimeout ? "var(--rbw-gray-light)" : "var(--rbw-blue)", color: "var(--rbw-navy)" }}
            >
              CREDIT
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Split: Enter Cash Amount ──
  if (showSplit && splitStep === "enter_cash") {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
        <div className="rounded-2xl shadow-2xl w-full max-w-md p-8" style={{ background: "var(--rbw-bg-card)" }}>
          <h2 className="text-2xl font-bold text-center mb-2" style={{ color: "var(--rbw-navy)" }}>
            Split Payment
          </h2>
          <p className="text-center text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
            Cash total: {fmt(totalCash)} &middot; Card total: {fmt(totalCard)}
          </p>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-1" style={{ color: "var(--rbw-navy)" }}>
              Cash Amount (collect this first)
            </label>
            <input
              type="number"
              value={splitCashInput}
              onChange={(e) => setSplitCashInput(e.target.value)}
              className="w-full px-4 py-3 rounded-lg text-2xl text-center outline-none"
              style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
              onFocus={(e) => e.target.style.borderColor = "var(--rbw-gold)"}
              onBlur={(e) => e.target.style.borderColor = "var(--rbw-border)"}
              placeholder="0.00"
              step="0.01"
              autoFocus
            />
          </div>

          {splitCashInput && Number(splitCashInput) > 0 && Number(splitCashInput) < totalCash && (
            <div className="rounded-lg p-3 mb-4 text-sm" style={{ background: "var(--rbw-bg)" }}>
              <div className="flex justify-between">
                <span>Cash portion:</span>
                <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>{fmt(Number(splitCashInput))}</span>
              </div>
              <div className="flex justify-between">
                <span>Card remainder (with 3% fee):</span>
                <span className="font-bold" style={{ color: "var(--rbw-blue-dark)" }}>
                  {(() => {
                    const c = Number(splitCashInput);
                    const frac = c / totalCash;
                    const cardSub = Number(sale.subtotal_cash) * (1 - frac);
                    const cardFee = cardSub * 0.03;
                    const cardTax = Number(sale.tax_cash) * (1 - frac);
                    return fmt(Math.round((cardSub + cardFee + cardTax) * 100) / 100);
                  })()}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-3 p-2 rounded-lg text-sm font-medium"
              style={{ background: "#fef2f2", color: "#dc2626" }}>
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => { setShowSplit(false); setError(null); }}
              className="flex-1 py-3 rounded-xl font-bold"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
              BACK
            </button>
            <button
              onClick={recordSplitCash}
              disabled={!splitCashInput || Number(splitCashInput) <= 0 || Number(splitCashInput) >= totalCash || processing}
              className="flex-1 py-3 rounded-xl font-bold disabled:opacity-30"
              style={{ background: "var(--rbw-navy)", color: "var(--rbw-gold)" }}>
              {processing ? "RECORDING..." : "COLLECT CASH & CONTINUE"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Payment Selection Screen ──
  const cardButtonsDisabled = processing || cardLockedAfterTimeout;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
      <div className="rounded-2xl shadow-2xl w-full max-w-lg p-8" style={{ background: "var(--rbw-bg-card)" }}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>Payment Method</h2>
          <button onClick={onClose} className="text-2xl" style={{ color: "var(--rbw-gray)" }}>&times;</button>
        </div>

        {/* Timeout warning */}
        {terminalState === "timeout" && cardLockedAfterTimeout && (
          <div className="mb-4 p-4 rounded-xl" style={{ background: "#FEF2F2", border: "2px solid #FECACA" }}>
            <div className="font-bold text-sm mb-2" style={{ color: "#DC2626" }}>
              TERMINAL TIMED OUT — CHARGE STATUS UNKNOWN
            </div>
            <div className="text-sm mb-3" style={{ color: "#991B1B" }}>
              Check the Helcim dashboard for a charge of {fmt(totalCard)} before retrying by card.
              Cash is safe to accept now.
            </div>
            <button
              onClick={() => { setCardLockedAfterTimeout(false); setTerminalState("idle"); }}
              className="w-full py-2.5 rounded-lg font-bold text-sm"
              style={{ background: "#DC2626", color: "#fff" }}
            >
              I checked Helcim — no charge went through — unlock card buttons
            </button>
          </div>
        )}

        {/* Partial auth warning */}
        {partialAuthMsg && (
          <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "#FEF3C7", border: "1px solid var(--rbw-gold)" }}>
            <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>{partialAuthMsg}</div>
            <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
              Collect the remaining {fmt(remainingAfterPartial)} by cash or another card.
            </div>
          </div>
        )}

        {/* Generic error/decline banner */}
        {error && (
          <div className="mb-4 p-3 rounded-lg text-sm font-medium flex justify-between items-center"
            style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
            <span>{error}</span>
            <button onClick={() => { setError(null); setTerminalState("idle"); }} className="ml-2 font-bold">&times;</button>
          </div>
        )}

        {totalDiscount > 0 && (
          <div className="mb-4 p-2 rounded-lg text-sm font-medium text-center"
            style={{ background: "#FEF3C7", color: "var(--rbw-navy)" }}>
            Discount applied: -{fmt(totalDiscount)}
          </div>
        )}

        {/* Price comparison. Once a gift card is on the sale these figures would
            be lying - the customer owes the remainder, not the full ticket - so
            the comparison is replaced by what is actually left to collect. */}
        {giftApplied > 0 && giftRemaining !== null && giftRemaining > 0 ? (
          <div className="rounded-xl p-4 mb-6 text-center" style={{ background: "var(--rbw-navy)", color: "#fff" }}>
            <div className="text-sm font-semibold uppercase" style={{ color: "var(--rbw-gold)" }}>
              Still to collect
            </div>
            <div className="text-4xl font-bold mt-1">{fmt(giftRemaining)}</div>
            <div className="text-xs mt-2" style={{ color: "var(--rbw-gold-light)" }}>
              Gift card already paid {fmt(giftApplied)} of {fmt(totalCash)}. Credit adds the service fee on this part only.
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="rounded-xl p-4" style={{ background: "var(--rbw-navy)", color: "#fff" }}>
            <div className="text-sm font-semibold uppercase" style={{ color: "var(--rbw-gold)" }}>Cash / Debit</div>
            <div className="text-3xl font-bold mt-1">{fmt(totalCash)}</div>
            <div className="text-xs mt-1" style={{ color: "var(--rbw-gold-light)" }}>Save ${savings}</div>
          </div>
          <div className="rounded-xl p-4" style={{ background: "var(--rbw-bg)", border: "2px solid var(--rbw-border)" }}>
            <div className="text-sm font-semibold uppercase" style={{ color: "var(--rbw-blue-dark)" }}>Credit Card</div>
            <div className="text-3xl font-bold mt-1" style={{ color: "var(--rbw-navy)" }}>{fmt(totalCard)}</div>
            <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>includes service fee</div>
          </div>
        </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <button onClick={() => processPayment("cash")} disabled={processing}
            className="py-8 rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-50"
            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
            CASH
          </button>
          <button onClick={() => processPayment("debit")} disabled={cardButtonsDisabled}
            className="py-8 rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-30"
            style={{ background: cardLockedAfterTimeout ? "var(--rbw-gray-light)" : "var(--rbw-gold-light)", color: "var(--rbw-navy)" }}>
            DEBIT
          </button>
          <button onClick={() => processPayment("credit")} disabled={cardButtonsDisabled}
            className="py-8 rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-30"
            style={{ background: cardLockedAfterTimeout ? "var(--rbw-gray-light)" : "var(--rbw-blue)", color: "var(--rbw-navy)" }}>
            CREDIT
          </button>
        </div>

        {tip > 0 && (
          <div className="mb-4 p-3 rounded-xl flex justify-between items-center" style={{ background: "#2D8A4E15", border: "1px solid var(--rbw-success)" }}>
            <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>
              Tip {fmt(tip)} goes on the card: debit {fmt(totalCash + tip)}, credit {fmt(totalCard + tip)}. Not added to cash.
            </span>
            <button onClick={() => { setTip(0); setTipInput(""); }} className="ml-2 text-sm font-bold" style={{ color: "var(--rbw-danger)" }}>
              REMOVE
            </button>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => { setShowTip(true); setShowGiftCard(false); setError(null); }} disabled={cardButtonsDisabled}
            className="py-4 rounded-xl font-bold text-lg transition-all active:scale-95 disabled:opacity-50"
            style={{ background: "var(--rbw-navy)", color: "#7CD4A0" }}>
            {tip > 0 ? "CHANGE TIP" : "ADD TIP"}
          </button>
          <button onClick={() => setShowSplit(true)} disabled={processing}
            className="py-4 rounded-xl font-bold text-lg transition-all active:scale-95 disabled:opacity-50"
            style={{ background: "var(--rbw-navy)", color: "var(--rbw-gray-light)" }}>
            SPLIT PAYMENT
          </button>
          <button onClick={() => { setShowGiftCard(true); setError(null); }} disabled={processing}
            className="py-4 rounded-xl font-bold text-lg transition-all active:scale-95 disabled:opacity-50"
            style={{ background: "var(--rbw-navy)", color: "var(--rbw-gold)" }}>
            GIFT CARD
          </button>
        </div>

        {showTip && (
          <div className="mt-4 p-4 rounded-xl" style={{ background: "var(--rbw-bg)", border: "2px solid var(--rbw-success)" }}>
            <div className="text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
              Tip on the card (debit or credit)
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[15, 18, 20].map((pct) => {
                const amt = Math.round(totalCash * pct) / 100;
                return (
                  <button key={pct} onClick={() => { setTip(amt); setTipInput(""); setShowTip(false); }}
                    className="py-3 rounded-xl font-bold transition-all active:scale-95"
                    style={{ background: "var(--rbw-bg-card)", border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}>
                    {pct}%<div className="text-xs font-normal">{fmt(amt)}</div>
                  </button>
                );
              })}
            </div>
            <input
              value={tipInput}
              onChange={(e) => setTipInput(e.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="Other amount, e.g. 2.00"
              className="w-full px-4 py-3 rounded-xl text-lg text-center outline-none mb-3"
              style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const amt = Math.round(Number(tipInput) * 100) / 100;
                  if (!(amt > 0)) { setError("Type the tip amount, or pick a percent."); return; }
                  setTip(amt); setTipInput(""); setShowTip(false); setError(null);
                }}
                disabled={!tipInput}
                className="flex-1 py-3 rounded-xl font-bold transition-all active:scale-95 disabled:opacity-50"
                style={{ background: "var(--rbw-success)", color: "#fff" }}>
                ADD TIP
              </button>
              <button onClick={() => { setShowTip(false); setTipInput(""); }}
                className="flex-1 py-3 rounded-xl font-bold transition-all active:scale-95"
                style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                CANCEL
              </button>
            </div>
          </div>
        )}

        {showGiftCard && (
          <div className="mt-4 p-4 rounded-xl" style={{ background: "var(--rbw-bg)", border: "2px solid var(--rbw-gold)" }}>
            <label className="block text-sm font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>
              Scan or type the gift card number
            </label>
            <input
              autoFocus
              value={giftCode}
              onChange={(e) => setGiftCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyGift(); } }}
              placeholder="Gift card number"
              className="w-full px-4 py-3 rounded-xl text-lg text-center outline-none mb-3"
              style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
            />
            <div className="flex gap-3">
              <button onClick={applyGift} disabled={giftBusy || !giftCode.trim()}
                className="flex-1 py-3 rounded-xl font-bold transition-all active:scale-95 disabled:opacity-50"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                {giftBusy ? "CHECKING..." : "USE CARD"}
              </button>
              <button onClick={() => { setShowGiftCard(false); setGiftCode(""); }}
                className="flex-1 py-3 rounded-xl font-bold transition-all active:scale-95"
                style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                CANCEL
              </button>
            </div>
          </div>
        )}

        {giftApplied > 0 && giftRemaining !== null && giftRemaining > 0 && (
          <div className="mt-4 p-3 rounded-xl text-center" style={{ background: "#FEF3C7", color: "var(--rbw-navy)" }}>
            <div className="font-bold">Gift card paid {fmt(giftApplied)}</div>
            <div className="text-lg font-bold mt-1">Still to collect: {fmt(giftRemaining)}</div>
            <div className="text-xs mt-1">Take the rest with cash, debit or credit above.</div>
          </div>
        )}
      </div>
    </div>
  );
}
